import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { Box, Container, Heading, Text, Button, VStack, Stack, SimpleGrid, List, ListItem, ListIcon, Flex, Badge, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Slider, SliderTrack, SliderFilledTrack, SliderThumb, IconButton } from '@chakra-ui/react';
import { CheckCircle2, XCircle, AlertCircle, ArrowRight, MessageCircle, Brain, Signal, Heart, HeartHandshake, BookOpen, Sparkles, Clock, Zap, Gift, Quote, Star, Play, Pause, Volume2 } from 'lucide-react';
// Audio URL
const AUDIO_URL = 'https://pikaso.cdnpk.net/private/production/3179765206/audio.mp3?token=exp=1770076800~hmac=a38fa27aae206587f454f72e598ba4a9a8c12dcb7f980adf47216617ee455851';
// E-book cover image URL
const EBOOK_COVER_URL = 'https://cdn.magicpatterns.com/uploads/kJ2emjQ2YdDx2EGnGE2Hq5/heylloyd-image-1769776142969.png';
// Hero couple image
const HERO_COUPLE_IMAGE = 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=600&h=800&fit=crop&crop=faces';
// Hotmart payment link
const HOTMART_LINK = 'https://pay.hotmart.com/E104172730A?checkoutMode=10';
// Meta Pixel ID
const META_PIXEL_ID = '4123428714467600';
// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
// Initialize Meta Pixel
const initMetaPixel = () => {
  if (typeof window !== 'undefined' && !window.fbq) {
    const f = window;
    const b = document;
    const e = 'script';
    const v = 'https://connect.facebook.net/en_US/fbevents.js';
    let n: any;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s?.parentNode?.insertBefore(t, s);
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
  }
};
// Track InitiateCheckout event
const trackInitiateCheckout = () => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'InitiateCheckout', {
      content_name: 'E-book Recuperalo Sin Rogar',
      content_category: 'E-book',
      value: 6.36,
      currency: 'USD'
    });
  }
};
// Reusable Section Component
const Section = ({
  children,
  bg = 'gray.900',
  id
}: {
  children: React.ReactNode;
  bg?: string;
  id?: string;
}) => <Box bg={bg} py={{
  base: 16,
  md: 24
}} id={id} color="white" data-id="element-1">
    <Container maxW="container.lg" data-id="element-2">{children}</Container>
  </Box>;
// Custom Audio Player Component
const AudioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };
  const handleSliderChange = (value: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  return <Box bg="gray.800" borderRadius="full" px={6} py={4} border="1px solid" borderColor="pink.500" boxShadow="0 4px 20px rgba(236, 72, 153, 0.2)" w={{
    base: 'full',
    md: 'auto'
  }} minW={{
    md: '400px'
  }} data-id="element-3">
      <audio ref={audioRef} src={AUDIO_URL} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={() => setIsPlaying(false)} data-id="element-4" />
      <Flex align="center" gap={4} data-id="element-5">
        {/* Play/Pause Button */}
        <IconButton aria-label={isPlaying ? 'Pausar' : 'Reproducir'} icon={isPlaying ? <Pause size={20} fill="white" data-id="element-7" /> : <Play size={20} fill="white" data-id="element-8" />} onClick={togglePlay} bg="pink.500" color="white" borderRadius="full" size="md" _hover={{
        bg: 'pink.400',
        transform: 'scale(1.05)'
      }} _active={{
        bg: 'pink.600'
      }} data-id="element-6" />

        {/* Progress Bar */}
        <Flex flex={1} align="center" gap={3} data-id="element-9">
          <Text fontSize="xs" color="gray.400" minW="35px" data-id="element-10">
            {formatTime(currentTime)}
          </Text>
          <Slider aria-label="audio-progress" value={currentTime} min={0} max={duration || 100} onChange={handleSliderChange} flex={1} data-id="element-11">
            <SliderTrack bg="gray.600" h="6px" borderRadius="full" data-id="element-12">
              <SliderFilledTrack bgGradient="linear(to-r, pink.500, red.400)" data-id="element-13" />
            </SliderTrack>
            <SliderThumb boxSize={4} bg="pink.400" border="2px solid white" data-id="element-14" />
          </Slider>
          <Text fontSize="xs" color="gray.400" minW="35px" data-id="element-15">
            {formatTime(duration)}
          </Text>
        </Flex>

        {/* Volume Icon */}
        <Volume2 size={18} color="#F687B3" data-id="element-16" />
      </Flex>
      <Text fontSize="xs" color="pink.300" textAlign="center" mt={2} data-id="element-17">
        🎧 Escuchá el mensaje de bienvenida
      </Text>
    </Box>;
};
// Primary CTA Button - Now with romantic pink/rose colors
const CtaButton = ({
  children,
  ...props
}: any) => {
  const handleClick = () => {
    trackInitiateCheckout();
  };
  return <Button as="a" href={HOTMART_LINK} target="_blank" rel="noopener noreferrer" onClick={handleClick} size="lg" bg="linear-gradient(135deg, #EC4899 0%, #F43F5E 50%, #EF4444 100%)" backgroundColor="pink.500" color="white" _hover={{
    bg: 'linear-gradient(135deg, #F472B6 0%, #FB7185 50%, #F87171 100%)',
    backgroundColor: 'pink.400',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px 0 rgba(236, 72, 153, 0.5)',
    textDecoration: 'none'
  }} _active={{
    bg: 'linear-gradient(135deg, #DB2777 0%, #E11D48 50%, #DC2626 100%)',
    backgroundColor: 'pink.600'
  }} fontSize={{
    base: 'md',
    md: 'lg'
  }} fontWeight="bold" px={10} py={7} h="auto" boxShadow="0 4px 25px 0 rgba(236, 72, 153, 0.4)" transition="all 0.3s ease" borderRadius="full" textTransform="uppercase" letterSpacing="wide" border="none" w={{
    base: 'full',
    md: 'auto'
  }} {...props} data-id="element-18">
      <Flex align="center" gap={3} data-id="element-19">
        {children}
        <Heart size={20} fill="white" color="white" data-id="element-20" />
      </Flex>
    </Button>;
};
export function LandingPage() {
  // Initialize Meta Pixel on component mount
  useEffect(() => {
    initMetaPixel();
  }, []);
  const scrollToPricing = () => {
    const element = document.getElementById('pricing');
    if (element) element.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <Box bg="gray.900" minH="100vh" fontFamily="sans-serif" position="relative" data-id="element-21">
      {/* BLOCK 1: HERO */}
      <Box as="section" position="relative" overflow="hidden" pt={{
      base: 20,
      md: 32
    }} pb={{
      base: 16,
      md: 24
    }} color="white" minH={{
      base: '100vh',
      md: 'auto'
    }} data-id="element-22">
        {/* Background Image - visible on all devices */}
        <Box position="absolute" top={0} right={0} bottom={0} left={0} zIndex={0} data-id="element-23">
          <Box position="absolute" top={0} right={0} bottom={0} left={0} backgroundImage={`url(${HERO_COUPLE_IMAGE})`} backgroundSize="cover" backgroundPosition="center" backgroundRepeat="no-repeat" data-id="element-24" />
          {/* Dark overlay for readability - lighter to show more image */}
          <Box position="absolute" top={0} right={0} bottom={0} left={0} bg="blackAlpha.600" data-id="element-25" />
          {/* Gradient overlay at bottom */}
          <Box position="absolute" bottom={0} left={0} right={0} h="50%" bgGradient="linear(to-t, gray.900, transparent)" data-id="element-26" />
        </Box>

        {/* Decorative glows */}
        <Box position="absolute" top="-20%" right="-10%" w="600px" h="600px" bg="pink.500" filter="blur(180px)" opacity="0.2" borderRadius="full" zIndex={1} data-id="element-27" />
        <Box position="absolute" bottom="-30%" left="-15%" w="500px" h="500px" bg="purple.500" filter="blur(150px)" opacity="0.15" borderRadius="full" zIndex={1} data-id="element-28" />

        <Container maxW="container.lg" position="relative" zIndex={2} data-id="element-29">
          <Flex direction={{
          base: 'column',
          lg: 'row'
        }} align="center" justify="space-between" gap={{
          base: 10,
          lg: 16
        }} data-id="element-30">
            <VStack spacing={8} align="start" maxW="3xl" flex={1} data-id="element-31">
              <Badge bg="pink.500" color="white" fontSize="sm" px={4} py={2} borderRadius="full" display="flex" alignItems="center" gap={2} data-id="element-32">
                <Sparkles size={14} data-id="element-33" />
                MÉTODO DOBLE CANAL™
              </Badge>
              <Heading as="h1" size="2xl" fontSize={{
              base: '3xl',
              md: '5xl',
              lg: '5xl'
            }} lineHeight="1.1" fontWeight="extrabold" textShadow="0 4px 20px rgba(0,0,0,0.5)" data-id="element-34">
                Si tenés que rogarle,{' '}
                <Text as="span" bgGradient="linear(to-r, pink.400, red.400)" bgClip="text" data-id="element-35">
                  ya perdiste.
                </Text>{' '}
                En 21 días dejás de perseguirlo y recuperás tu poder.
              </Heading>
              <Text fontSize={{
              base: 'lg',
              md: 'xl'
            }} color="white" lineHeight="tall" maxW="2xl" textShadow="0 2px 10px rgba(0,0,0,0.5)" data-id="element-36">
                Método Mente + Señales (Doble Canal™) para cortar la urgencia
                (esa que él nota aunque no la digas) y volver a ser la mujer que
                elige, no la que espera. Solo 10–20 min/día.
              </Text>

              <VStack align="start" spacing={3} w="full" data-id="element-37">
                <CtaButton onClick={scrollToPricing} data-id="element-38">
                  QUIERO EMPEZAR HOY
                </CtaButton>
                <Text fontSize="xs" color="pink.200" fontWeight="medium" textShadow="0 1px 5px rgba(0,0,0,0.5)" data-id="element-39">
                  💕 DIGITAL • ACCESO INMEDIATO • LATAM
                </Text>

                {/* Audio Player */}
                <Box mt={4} w="full" data-id="element-40">
                  <AudioPlayer data-id="element-41" />
                </Box>
              </VStack>
            </VStack>

            {/* Hero Image - Couple (Desktop only - framed version) */}
            <Box flex={1} display={{
            base: 'none',
            lg: 'block'
          }} position="relative" maxW="400px" data-id="element-42">
              <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" w="350px" h="350px" bg="pink.500" filter="blur(100px)" opacity="0.3" borderRadius="full" zIndex={0} data-id="element-43" />
              <Box position="relative" zIndex={1} borderRadius="2xl" overflow="hidden" border="3px solid" borderColor="pink.400" boxShadow="0 25px 60px rgba(236, 72, 153, 0.3)" transform="rotate(2deg)" _hover={{
              transform: 'rotate(0deg) scale(1.02)'
            }} transition="all 0.4s ease" data-id="element-44">
                <img src={HERO_COUPLE_IMAGE} alt="Pareja abrazada" style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                filter: 'brightness(0.9)'
              }} data-id="element-45" />
                {/* Overlay gradient */}
                <Box position="absolute" bottom={0} left={0} right={0} h="40%" bgGradient="linear(to-t, blackAlpha.700, transparent)" data-id="element-46" />
              </Box>
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* BLOCK 2: PAIN POINTS */}
      <Section bg="gray.800" data-id="element-47">
        <VStack spacing={10} data-id="element-48">
          <Heading as="h2" size="xl" textAlign="center" maxW="2xl" lineHeight="shorter" data-id="element-49">
            Esto es lo que te está pasando{' '}
            <Text as="span" color="pink.300" data-id="element-50">
              (y lo sabés):
            </Text>
          </Heading>

          <SimpleGrid columns={{
          base: 1,
          md: 2
        }} spacing={8} w="full" data-id="element-51">
            <VStack spacing={6} align="stretch" data-id="element-52">
              {['Te clava el visto → entrás en espiral pensando qué hiciste mal', 'Le mandás "¿todo bien?" → después "perdón si molesté"', 'Explicás de más para que no se aleje de vos', 'Stalkeás su Instagram para calmarte (y terminás peor)', 'Te prometés "no le escribo más" y a las horas recaés'].map((item, index) => <Flex key={index} bg="gray.700" p={4} borderRadius="lg" align="center" borderLeft="4px solid" borderColor="pink.400" _hover={{
              bg: 'gray.650',
              borderColor: 'pink.300'
            }} transition="all 0.2s" data-id="element-53">
                  <AlertCircle color="#F687B3" size={24} style={{
                minWidth: '24px'
              }} data-id="element-54" />
                  <Text ml={4} fontWeight="medium" color="gray.100" data-id="element-55">
                    {item}
                  </Text>
                </Flex>)}
            </VStack>
            <Flex direction="column" justify="center" align="center" bgGradient="linear(to-br, gray.900, purple.900)" p={8} borderRadius="xl" border="1px solid" borderColor="pink.500" textAlign="center" boxShadow="0 0 40px rgba(236, 72, 153, 0.15)" data-id="element-56">
              <Heart size={64} color="#F687B3" fill="#F687B3" style={{
              opacity: 0.8
            }} data-id="element-57" />
              <Heading size="lg" mt={6} mb={4} color="white" data-id="element-58">
                No estás enamorada.
              </Heading>
              <Text fontSize="xl" color="pink.300" fontWeight="bold" data-id="element-59">
                Estás activada.
              </Text>
            </Flex>
          </SimpleGrid>
        </VStack>
      </Section>

      {/* BLOCK 3: UNCOMFORTABLE TRUTH */}
      <Box as="section" bgGradient="linear(to-b, black, purple.900, black)" py={{
      base: 16,
      md: 24
    }} color="white" data-id="element-60">
        <Container maxW="container.md" textAlign="center" data-id="element-61">
          <VStack spacing={6} data-id="element-62">
            <Heading as="h3" size="lg" color="white" data-id="element-63">
              El problema no es tu mensaje.
              <br data-id="element-64" />
              <Text as="span" bgGradient="linear(to-r, pink.400, red.400)" bgClip="text" data-id="element-65">
                El problema es la urgencia detrás.
              </Text>
            </Heading>
            <Text fontSize="lg" color="gray.400" data-id="element-66">
              La urgencia comunica:
            </Text>
            <Text fontSize="2xl" fontWeight="bold" fontStyle="italic" color="white" bg="gray.800" px={8} py={4} borderRadius="md" borderLeft="4px solid" borderColor="pink.400" data-id="element-67">
              "Necesito que me respondas para estar bien."
            </Text>
            <Text fontSize="lg" color="gray.400" data-id="element-68">
              Y eso, aunque duela, te hace menos atractiva para él.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* BLOCK 4: SOLUTION */}
      <Section bg="gray.900" data-id="element-69">
        <VStack spacing={12} data-id="element-70">
          <Box textAlign="center" maxW="2xl" data-id="element-71">
            <Heading size="xl" mb={4} data-id="element-72">
              La solución no es escribirle menos.{' '}
              <Text as="span" bgGradient="linear(to-r, pink.400, purple.400)" bgClip="text" data-id="element-73">
                Es regularte primero.
              </Text>
            </Heading>
            <Text fontSize="xl" color="gray.300" data-id="element-74">
              Método Doble Canal™: Calma primero → señal después.
            </Text>
          </Box>

          <SimpleGrid columns={{
          base: 1,
          md: 2
        }} spacing={8} w="full" data-id="element-75">
            <Box bg="gray.800" p={8} borderRadius="xl" borderTop="4px solid" borderColor="purple.400" _hover={{
            transform: 'translateY(-4px)',
            boxShadow: '0 10px 40px rgba(159, 122, 234, 0.2)'
          }} transition="all 0.3s" data-id="element-76">
              <Flex align="center" mb={6} data-id="element-77">
                <Box p={3} bg="purple.900" borderRadius="lg" mr={4} data-id="element-78">
                  <Brain color="#B794F4" size={32} data-id="element-79" />
                </Box>
                <Heading size="md" data-id="element-80">Mente</Heading>
              </Flex>
              <Text color="gray.300" fontSize="lg" data-id="element-81">
                Bajás la ansiedad, cortás los pensamientos obsesivos, recuperás
                tu centro. Dejás de actuar desde el miedo a perderlo.
              </Text>
            </Box>

            <Box bg="gray.800" p={8} borderRadius="xl" borderTop="4px solid" borderColor="pink.400" _hover={{
            transform: 'translateY(-4px)',
            boxShadow: '0 10px 40px rgba(236, 72, 153, 0.2)'
          }} transition="all 0.3s" data-id="element-82">
              <Flex align="center" mb={6} data-id="element-83">
                <Box p={3} bg="pink.900" borderRadius="lg" mr={4} data-id="element-84">
                  <Signal color="#F687B3" size={32} data-id="element-85" />
                </Box>
                <Heading size="md" data-id="element-86">Señales</Heading>
              </Flex>
              <Text color="gray.300" fontSize="lg" data-id="element-87">
                Ritmo, límites, disponibilidad calibrada. Te volvés
                misteriosamente estable, y eso lo intriga.
              </Text>
            </Box>
          </SimpleGrid>

          <CtaButton onClick={scrollToPricing} data-id="element-88">SÍ, LO NECESITO</CtaButton>
        </VStack>
      </Section>

      {/* BLOCK 5: WHAT'S INCLUDED */}
      <Section bg="gray.800" data-id="element-89">
        <Flex direction={{
        base: 'column',
        lg: 'row'
      }} align="center" justify="space-between" gap={12} data-id="element-90">
          <Box flex={1} data-id="element-91">
            <Heading size="xl" mb={8} data-id="element-92">
              Qué incluye el e-book
            </Heading>
            <List spacing={5} data-id="element-93">
              {['Plan guiado de 21 días (10–20 min/día)', '3 reglas anti-autosabotaje (textos largos, pedir claridad, stalkeo)', 'Checklist de urgencia (detectás cuando estás actuando desde la ansiedad)', 'Plantillas: qué decirle / qué NO decirle', 'Protocolos anti-recaída para cuando querés escribirle', 'Termómetro cada 3 días: ansiedad / impulso / autorespeto / foco'].map((item, index) => <ListItem key={index} display="flex" alignItems="start" data-id="element-94">
                  <ListIcon as={BookOpen} color="pink.400" mt={1} data-id="element-95" />
                  <Text fontSize="lg" color="gray.200" data-id="element-96">
                    {item}
                  </Text>
                </ListItem>)}
            </List>
          </Box>
          {/* E-book Cover Image */}
          <Box flex={1} w="full" maxW="400px" position="relative" data-id="element-97">
            {/* Urgency Badge */}
            <Badge position="absolute" top={-3} right={-3} bg="red.500" color="white" fontSize="sm" px={4} py={2} borderRadius="full" zIndex={2} display="flex" alignItems="center" gap={2} animation="pulse 2s infinite" boxShadow="0 0 20px rgba(239, 68, 68, 0.5)" data-id="element-98">
              <Zap size={14} fill="white" data-id="element-99" />
              OFERTA HOY
            </Badge>

            <Box borderRadius="xl" overflow="hidden" boxShadow="0 25px 50px -12px rgba(236, 72, 153, 0.35)" border="2px solid" borderColor="pink.500" transition="transform 0.3s" _hover={{
            transform: 'scale(1.02)'
          }} data-id="element-100">
              <img src={EBOOK_COVER_URL} alt="E-book: Recuperalo Sin Rogar - Método Doble Canal" style={{
              width: '100%',
              height: 'auto',
              display: 'block'
            }} data-id="element-101" />
            </Box>

            {/* CTA Button with urgency */}
            <VStack spacing={3} mt={6} w="full" data-id="element-102">
              <CtaButton onClick={scrollToPricing} w="full" data-id="element-103">
                QUIERO MI COPIA AHORA
              </CtaButton>
              <Flex align="center" gap={2} bg="red.900" px={4} py={2} borderRadius="full" border="1px solid" borderColor="red.500" data-id="element-104">
                <Clock size={14} color="#FC8181" data-id="element-105" />
                <Text color="red.200" fontSize="sm" fontWeight="medium" data-id="element-106">
                  ⚡ Precio especial por tiempo limitado
                </Text>
              </Flex>
            </VStack>
          </Box>
        </Flex>
      </Section>

      {/* BLOCK 6: BENEFITS */}
      <Section bg="gray.900" data-id="element-107">
        <VStack spacing={12} data-id="element-108">
          <Heading size="xl" textAlign="center" data-id="element-109">
            Qué lográs en 21 días
          </Heading>
          <SimpleGrid columns={{
          base: 1,
          md: 3
        }} spacing={8} w="full" data-id="element-110">
            {[{
            title: 'Control Emocional',
            desc: 'Dejás de mandarle mensajes desde el pánico. Sus silencios ya no te destruyen.',
            icon: Brain,
            color: '#B794F4'
          }, {
            title: 'Autorespeto',
            desc: 'Recuperás tu dignidad sin hacerte la fría. Volvés a ser vos misma.',
            icon: HeartHandshake,
            color: '#F687B3'
          }, {
            title: 'Comunicación',
            desc: 'Si le hablás, lo hacés breve, segura y sin rogar. Sin reclamos ni interrogatorios.',
            icon: MessageCircle,
            color: '#FC8181'
          }].map((benefit, idx) => <VStack key={idx} bg="gray.800" p={8} borderRadius="lg" align="start" spacing={4} transition="all 0.3s" _hover={{
            transform: 'translateY(-5px)',
            boxShadow: `0 15px 40px rgba(236, 72, 153, 0.15)`
          }} border="1px solid" borderColor="gray.700" data-id="element-111">
                <Box p={3} bg="gray.700" borderRadius="full" data-id="element-112">
                  <benefit.icon color={benefit.color} size={24} data-id="element-113" />
                </Box>
                <Heading size="md" data-id="element-114">{benefit.title}</Heading>
                <Text color="gray.400" data-id="element-115">{benefit.desc}</Text>
              </VStack>)}
          </SimpleGrid>
          <Box bgGradient="linear(to-r, pink.900, purple.900)" p={6} borderRadius="lg" borderLeft="4px solid" borderColor="pink.400" data-id="element-116">
            <Text color="pink.100" fontWeight="medium" data-id="element-117">
              💝 Importante: no promete que él vuelva. Promete que vos volvés a
              estar en control de tus emociones y tu vida.
            </Text>
          </Box>
        </VStack>
      </Section>

      {/* BLOCK 7: WHO IS IT FOR */}
      <Section bg="gray.800" data-id="element-118">
        <SimpleGrid columns={{
        base: 1,
        md: 2
      }} spacing={12} data-id="element-119">
          <VStack align="start" spacing={6} data-id="element-120">
            <Heading size="lg" color="pink.300" data-id="element-121">
              💕 Para quién es
            </Heading>
            <List spacing={4} data-id="element-122">
              {['Para vos si estás enganchada con un ex, un "casi algo" o un ida y vuelta.', 'Para vos si estás cansada de humillarte por ansiedad.', 'Para vos si querés recuperar tu poder y dejar de depender de su respuesta.'].map((item, i) => <ListItem key={i} display="flex" alignItems="center" data-id="element-123">
                  <ListIcon as={CheckCircle2} color="pink.400" boxSize={6} data-id="element-124" />
                  <Text fontSize="lg" data-id="element-125">{item}</Text>
                </ListItem>)}
            </List>
          </VStack>

          <VStack align="start" spacing={6} data-id="element-126">
            <Heading size="lg" color="gray.400" data-id="element-127">
              Para quién NO es
            </Heading>
            <List spacing={4} data-id="element-128">
              {['No es para manipularlo ni forzar que te quiera.', 'No es una fórmula mágica para que vuelva.', 'No es para mujeres que no quieren hacer trabajo interno.'].map((item, i) => <ListItem key={i} display="flex" alignItems="center" data-id="element-129">
                  <ListIcon as={XCircle} color="gray.500" boxSize={6} data-id="element-130" />
                  <Text fontSize="lg" color="gray.400" data-id="element-131">
                    {item}
                  </Text>
                </ListItem>)}
            </List>
          </VStack>
        </SimpleGrid>
      </Section>

      {/* BLOCK 7.5: TESTIMONIALS */}
      <Section bg="gray.900" data-id="element-132">
        <VStack spacing={12} data-id="element-133">
          <VStack spacing={4} textAlign="center" data-id="element-134">
            <Badge bg="pink.500" color="white" fontSize="sm" px={4} py={2} borderRadius="full" data-id="element-135">
              💬 TESTIMONIOS REALES
            </Badge>
            <Heading size="xl" data-id="element-136">
              Mujeres que{' '}
              <Text as="span" color="pink.400" data-id="element-137">
                recuperaron su poder
              </Text>
            </Heading>
          </VStack>

          <SimpleGrid columns={{
          base: 1,
          md: 3
        }} spacing={8} w="full" data-id="element-138">
            {[{
            name: 'Camila, 28 años',
            location: 'Buenos Aires',
            text: 'Llevaba meses en un ida y vuelta que me destruía. Después del día 7 dejé de revisar su última conexión. Al día 15, él me escribió. Pero lo más importante: ya no me importaba tanto. Recuperé mi paz.',
            stars: 5,
            image: 'https://cdn.magicpatterns.com/uploads/i2HyfaP723r1URNgb2bwaa/descarga_(10).jpg'
          }, {
            name: 'Valentina, 32 años',
            location: 'México',
            text: 'Me daba vergüenza admitir que stalkeaba a mi ex 20 veces al día. Este método me dio herramientas concretas. Hoy puedo ver su foto sin que me tiemble el pulso. Eso no tiene precio.',
            stars: 5,
            image: 'https://cdn.magicpatterns.com/uploads/r1X3kF6HAi1GcBhYMZfBQE/descarga_(9).jpg'
          }, {
            name: 'Luciana, 25 años',
            location: 'Colombia',
            text: 'Pensé que era cursi, pero funcionó. Los protocolos anti-recaída me salvaron varias veces. Ahora cuando siento la urgencia, sé exactamente qué hacer. Ya no actúo desde el pánico.',
            stars: 5,
            image: 'https://cdn.magicpatterns.com/uploads/nKyTiyGHesStFoPt2XF2js/descarga_(8).jpg'
          }].map((testimonial, idx) => <Box key={idx} bg="gray.800" p={8} borderRadius="xl" border="1px solid" borderColor="gray.700" position="relative" transition="all 0.3s" _hover={{
            transform: 'translateY(-5px)',
            borderColor: 'pink.500',
            boxShadow: '0 15px 40px rgba(236, 72, 153, 0.15)'
          }} data-id="element-139">
                {/* Quote icon */}
                <Box position="absolute" top={4} right={4} color="pink.500" opacity={0.3} data-id="element-140">
                  <Quote size={40} fill="currentColor" data-id="element-141" />
                </Box>

                {/* Stars */}
                <Flex mb={4} gap={1} data-id="element-142">
                  {[...Array(testimonial.stars)].map((_, i) => <Star key={i} size={18} fill="#F687B3" color="#F687B3" data-id="element-143" />)}
                </Flex>

                {/* Testimonial text */}
                <Text color="gray.300" fontSize="md" lineHeight="tall" mb={6} fontStyle="italic" data-id="element-144">
                  "{testimonial.text}"
                </Text>

                {/* Author */}
                <Flex align="center" gap={3} data-id="element-145">
                  <Box w={12} h={12} borderRadius="full" overflow="hidden" border="2px solid" borderColor="pink.400" flexShrink={0} data-id="element-146">
                    <img src={testimonial.image} alt={testimonial.name} style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }} data-id="element-147" />
                  </Box>
                  <Box data-id="element-148">
                    <Text color="white" fontWeight="bold" fontSize="sm" data-id="element-149">
                      {testimonial.name}
                    </Text>
                    <Text color="gray.500" fontSize="xs" data-id="element-150">
                      {testimonial.location}
                    </Text>
                  </Box>
                </Flex>
              </Box>)}
          </SimpleGrid>

          {/* Social proof */}
          <Flex align="center" gap={4} bg="gray.800" px={6} py={4} borderRadius="full" border="1px solid" borderColor="pink.500" data-id="element-151">
            <Flex data-id="element-152">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="#F687B3" color="#F687B3" data-id="element-153" />)}
            </Flex>
            <Text color="gray.300" fontSize="sm" data-id="element-154">
              +500 mujeres ya transformaron su situación
            </Text>
          </Flex>

          <CtaButton onClick={scrollToPricing} data-id="element-155">QUIERO SER LA PRÓXIMA</CtaButton>
        </VStack>
      </Section>

      {/* BLOCK 8: FAQ */}
      <Section bg="gray.900" data-id="element-156">
        <Heading size="xl" mb={10} textAlign="center" data-id="element-157">
          Preguntas Frecuentes
        </Heading>
        <Container maxW="container.md" data-id="element-158">
          <Accordion allowMultiple data-id="element-159">
            {[{
            q: '¿Y si por no escribirle lo pierdo?',
            a: 'Si le escribís desde la urgencia, igual lo estás alejando. Primero calma, después acción. La desesperación repele, la estabilidad atrae.'
          }, {
            q: '¿Es contacto cero?',
            a: 'No necesariamente. Es un sistema para no actuar desde la ansiedad y emitir una señal estable. A veces implica silencio, a veces comunicación neutra.'
          }, {
            q: '¿Y si recaigo y le escribo?',
            a: 'Tenés protocolos específicos para eso. El método incluye herramientas para volver a tu centro rápidamente si tenés un desliz.'
          }].map((faq, idx) => <AccordionItem key={idx} border="none" mb={4} bg="gray.800" borderRadius="lg" data-id="element-160">
                <h2 data-id="element-161">
                  <AccordionButton py={6} _hover={{
                bg: 'gray.700'
              }} borderRadius="lg" data-id="element-162">
                    <Box flex="1" textAlign="left" fontWeight="bold" fontSize="lg" data-id="element-163">
                      {faq.q}
                    </Box>
                    <AccordionIcon color="pink.400" data-id="element-164" />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={6} color="gray.300" data-id="element-165">
                  {faq.a}
                </AccordionPanel>
              </AccordionItem>)}
          </Accordion>
        </Container>
      </Section>

      {/* BLOCK 9: FINAL CTA */}
      <Box as="section" id="pricing" bgGradient="linear(to-b, gray.900, purple.900, black)" py={24} textAlign="center" borderTop="1px solid" borderColor="pink.800" position="relative" overflow="hidden" data-id="element-166">
        {/* Romantic glow effect */}
        <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" w="400px" h="400px" bg="pink.500" filter="blur(120px)" opacity="0.15" borderRadius="full" data-id="element-167" />

        <Container maxW="container.lg" position="relative" zIndex={1} data-id="element-168">
          <VStack spacing={10} data-id="element-169">
            {/* Urgency Banner */}
            <Box bg="red.500" color="white" px={6} py={3} borderRadius="full" display="flex" alignItems="center" gap={3} animation="pulse 2s infinite" boxShadow="0 0 30px rgba(239, 68, 68, 0.4)" data-id="element-170">
              <Clock size={20} data-id="element-171" />
              <Text fontWeight="bold" fontSize="lg" data-id="element-172">
                ⚡ OFERTA VÁLIDA SOLO POR HOY ⚡
              </Text>
            </Box>

            <Heading size="xl" color="white" lineHeight="shorter" data-id="element-173">
              No necesitás otra charla con tus amigas.
              <br data-id="element-174" />
              <Text as="span" bgGradient="linear(to-r, pink.400, red.400)" bgClip="text" data-id="element-175">
                Necesitás dejar de actuar desde la ansiedad.
              </Text>
            </Heading>

            {/* Pricing Section with E-book */}
            <Flex direction={{
            base: 'column',
            md: 'row'
          }} align="center" justify="center" gap={10} w="full" data-id="element-176">
              {/* E-book Cover Mini */}
              <Box w={{
              base: '200px',
              md: '250px'
            }} borderRadius="lg" overflow="hidden" boxShadow="0 20px 40px rgba(236, 72, 153, 0.3)" border="2px solid" borderColor="pink.400" flexShrink={0} data-id="element-177">
                <img src={EBOOK_COVER_URL} alt="E-book Recuperalo Sin Rogar" style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }} data-id="element-178" />
              </Box>

              {/* Pricing Card */}
              <VStack bg="gray.800" p={8} borderRadius="2xl" border="2px solid" borderColor="pink.500" spacing={6} maxW="400px" w="full" position="relative" boxShadow="0 0 60px rgba(236, 72, 153, 0.2)" data-id="element-179">
                {/* Discount Badge */}
                <Badge position="absolute" top={-4} bg="linear-gradient(135deg, #EC4899 0%, #EF4444 100%)" backgroundColor="pink.500" color="white" fontSize="md" px={5} py={2} borderRadius="full" fontWeight="bold" boxShadow="0 4px 15px rgba(236, 72, 153, 0.4)" data-id="element-180">
                  <Flex align="center" gap={2} data-id="element-181">
                    <Gift size={16} data-id="element-182" />
                    PRECIO ESPECIAL DE LANZAMIENTO
                  </Flex>
                </Badge>

                <VStack spacing={1} pt={4} data-id="element-183">
                  <Text color="gray.400" textDecoration="line-through" fontSize="lg" data-id="element-184">
                    $19.99 USD
                  </Text>
                  <Flex align="baseline" gap={2} data-id="element-185">
                    <Text fontSize="5xl" fontWeight="extrabold" bgGradient="linear(to-r, pink.400, red.400)" bgClip="text" data-id="element-186">
                      $6.36
                    </Text>
                    <Text color="gray.300" fontSize="lg" data-id="element-187">
                      USD
                    </Text>
                  </Flex>
                  <Text color="pink.300" fontSize="sm" fontWeight="medium" data-id="element-188">
                    ¡Ahorrás más del 68%!
                  </Text>
                </VStack>

                <VStack spacing={3} w="full" data-id="element-189">
                  <List spacing={2} textAlign="left" w="full" data-id="element-190">
                    {['Acceso inmediato al e-book completo', 'Plan de 21 días paso a paso', 'Plantillas y protocolos incluidos', 'Actualizaciones gratuitas'].map((item, i) => <ListItem key={i} display="flex" alignItems="center" fontSize="sm" data-id="element-191">
                        <ListIcon as={CheckCircle2} color="pink.400" data-id="element-192" />
                        <Text color="gray.300" data-id="element-193">{item}</Text>
                      </ListItem>)}
                  </List>
                </VStack>

                <Box w="full" data-id="element-194">
                  <CtaButton w="full" size="lg" py={7} data-id="element-195">
                    DESCARGAR AHORA
                  </CtaButton>
                </Box>

                {/* Countdown urgency */}
                <Box bg="red.900" px={4} py={2} borderRadius="md" border="1px solid" borderColor="red.500" data-id="element-196">
                  <Flex align="center" gap={2} data-id="element-197">
                    <Clock size={16} color="#FC8181" data-id="element-198" />
                    <Text color="red.200" fontSize="sm" fontWeight="medium" data-id="element-199">
                      🔥 Esta oferta expira en pocas horas
                    </Text>
                  </Flex>
                </Box>
              </VStack>
            </Flex>

            <Stack direction={{
            base: 'column',
            sm: 'row'
          }} spacing={4} align="center" justify="center" color="pink.200" fontSize="sm" data-id="element-200">
              <Flex align="center" data-id="element-201">
                <CheckCircle2 size={14} style={{
                marginRight: '4px'
              }} data-id="element-202" />{' '}
                Acceso inmediato
              </Flex>
              <Text display={{
              base: 'none',
              sm: 'block'
            }} data-id="element-203">
                •
              </Text>
              <Flex align="center" data-id="element-204">
                <CheckCircle2 size={14} style={{
                marginRight: '4px'
              }} data-id="element-205" />{' '}
                Digital
              </Flex>
              <Text display={{
              base: 'none',
              sm: 'block'
            }} data-id="element-206">
                •
              </Text>
              <Flex align="center" data-id="element-207">
                <CheckCircle2 size={14} style={{
                marginRight: '4px'
              }} data-id="element-208" />{' '}
                Compra segura por Hotmart
              </Flex>
            </Stack>
          </VStack>
        </Container>
      </Box>
    </Box>;
}